import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { orders, type OrderRequest } from "@/api/orders";
import { ApiError } from "@/api/http";
import { useActiveAccount } from "@/components/ActiveAccount";
import { downloadCsv } from "@/lib/csv";
import { Badge, Button, Card, CardBody, FormError, Input, Label, Select } from "@/components/ui";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useWsSubscribe } from "@/ws/WebSocketProvider";

export function OrdersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [modifyTarget, setModifyTarget] = useState<string | null>(null);

  const closeMutation = useMutation({
    mutationFn: (order_id: string) => orders.close(order_id),
    onSuccess: () => {
      toast.push("Close submitted", "warn");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: () => orders.list(),
    // 30s fallback; WebSocket fill events drive most refreshes.
    refetchInterval: 30_000,
  });

  // Live fill events: toast + invalidate so the table refreshes immediately.
  useWsSubscribe("fill", (msg) => {
    const symbol = String(msg.symbol ?? "?");
    const side = String(msg.side ?? "?");
    const qty = String(msg.qty ?? "");
    const price = String(msg.price ?? "");
    toast.push(`Fill: ${side} ${qty} ${symbol} @ ${price}`, "success");
    qc.invalidateQueries({ queryKey: ["orders"] });
  });

  // Engine alerts: rejections, stops triggered, circuit breakers.
  useWsSubscribe("alert", (msg) => {
    const level = String(msg.level ?? "info");
    const tone = level === "error" ? "error" : level === "warning" ? "warn" : level === "success" ? "success" : "info";
    toast.push(String(msg.message ?? ""), tone as "info" | "warn" | "error" | "success");
  });

  const flatten = useMutation({
    mutationFn: () => orders.flatten(),
    onSuccess: () => {
      toast.push("Flatten submitted", "warn");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Orders</h1>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              if (data?.orders.length) {
                downloadCsv(
                  data.orders as unknown as Record<string, unknown>[],
                  `orders-${new Date().toISOString().slice(0, 10)}`,
                );
              } else {
                toast.push("No orders to export", "info");
              }
            }}
            disabled={!data || data.orders.length === 0}
          >
            Export CSV
          </Button>
          <Button variant="danger" onClick={() => flatten.mutate()} loading={flatten.isPending}>
            Flatten all
          </Button>
          <Button onClick={() => setOpen(true)}>Place order</Button>
        </div>
      </div>

      <RestingLimitsCard />

      <Card>
        <CardBody className="overflow-x-auto">
          {isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : data?.orders.length === 0 ? (
            <p className="text-sm text-slate-500">No orders yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Order</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Qty</th>
                  <th>Filled</th>
                  <th>Avg fill</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.orders.map((o) => {
                  const isOpen = !["FILLED", "REJECTED", "CANCELED"].includes(o.status);
                  return (
                    <tr key={o.order_id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-2 font-mono text-xs">{o.order_id.slice(0, 8)}</td>
                      <td className="font-medium">{o.symbol}</td>
                      <td>
                        <Badge tone={o.side === "BUY" ? "good" : "bad"}>{o.side}</Badge>
                      </td>
                      <td>{o.qty}</td>
                      <td>{o.filled_qty}</td>
                      <td>{o.avg_fill_price ?? "—"}</td>
                      <td>
                        <Badge tone={o.status === "FILLED" ? "good" : o.status === "REJECTED" ? "bad" : "neutral"}>
                          {o.status}
                        </Badge>
                      </td>
                      <td className="text-xs text-slate-500">
                        {new Date(o.created_at).toLocaleString()}
                      </td>
                      <td className="text-right">
                        {isOpen && (
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" onClick={() => setModifyTarget(o.order_id)}>
                              Modify
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => closeMutation.mutate(o.order_id)}
                              disabled={closeMutation.isPending}
                            >
                              Close
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <PlaceOrderModal open={open} onClose={() => setOpen(false)} />
      <ModifyOrderModal
        orderId={modifyTarget}
        onClose={() => setModifyTarget(null)}
      />
    </div>
  );
}


function ModifyOrderModal({
  orderId,
  onClose,
}: {
  orderId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      orders.modify(orderId!, {
        stop_loss: sl ? Number(sl) : null,
        take_profit: tp ? Number(tp) : null,
      }),
    onSuccess: () => {
      toast.push("Order modified", "success");
      qc.invalidateQueries({ queryKey: ["orders"] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open={orderId !== null} onClose={onClose} title="Modify SL / TP">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Stop loss</Label>
            <Input
              type="number"
              step="any"
              value={sl}
              onChange={(e) => setSl(e.target.value)}
              placeholder="leave blank to clear"
            />
          </div>
          <div>
            <Label>Take profit</Label>
            <Input
              type="number"
              step="any"
              value={tp}
              onChange={(e) => setTp(e.target.value)}
              placeholder="leave blank to clear"
            />
          </div>
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={submit.isPending}>Apply</Button>
        </div>
      </form>
    </Modal>
  );
}

function RestingLimitsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const { active } = useActiveAccount();

  const limits = useQuery({
    queryKey: ["limit-orders", active?.id],
    queryFn: () => orders.listLimits(active?.id),
    enabled: true,
    refetchInterval: 30_000,
  });

  // Refresh on internal-fill / fill so the panel doesn't show a
  // stale resting limit after it executes.
  useWsSubscribe("internal_fill", () =>
    qc.invalidateQueries({ queryKey: ["limit-orders"] }),
  );
  useWsSubscribe("fill", () =>
    qc.invalidateQueries({ queryKey: ["limit-orders"] }),
  );

  const cancel = useMutation({
    mutationFn: (id: string) => orders.cancelLimit(id),
    onSuccess: () => {
      toast.push("Limit canceled", "warn");
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  const cancelAll = useMutation({
    mutationFn: async () => {
      if (!limits.data || limits.data.length === 0) return null;
      const results = await Promise.allSettled(
        limits.data.map((o) => orders.cancelLimit(o.id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      return { total: limits.data.length, failed };
    },
    onSuccess: (r) => {
      if (!r) return;
      const ok = r.total - r.failed;
      toast.push(
        r.failed === 0
          ? `Canceled ${ok}/${r.total} limit orders`
          : `Canceled ${ok}/${r.total} (${r.failed} failed)`,
        r.failed === 0 ? "warn" : "error",
      );
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
    },
  });

  return (
    <Card>
      <CardBody className="overflow-x-auto p-0">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-3 py-2">
          <h2 className="text-sm font-semibold">
            Resting limits ({limits.data?.length ?? 0})
          </h2>
          {limits.data && limits.data.length > 0 && (
            <Button
              variant="danger"
              onClick={() => {
                if (confirm(`Cancel all ${limits.data!.length} resting limits?`))
                  cancelAll.mutate();
              }}
              disabled={cancelAll.isPending}
            >
              Cancel all
            </Button>
          )}
        </div>
        {limits.isLoading ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : !limits.data || limits.data.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            No resting limit orders. Submit a LIMIT order from the place form
            and it'll show here until filled.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Symbol</th>
                <th className="px-3 py-2">Side</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {limits.data.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-slate-200 dark:border-slate-800"
                >
                  <td className="px-3 py-2 font-medium">{o.symbol}</td>
                  <td className="px-3 py-2">
                    <Badge tone={o.side === "BUY" ? "good" : "bad"}>{o.side}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right tnum">
                    {o.qty_remaining}
                    {o.qty_remaining !== o.qty_original && (
                      <span className="ml-1 text-2xs text-slate-500">
                        / {o.qty_original}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tnum">{o.price}</td>
                  <td className="px-3 py-2">
                    <Badge tone={o.status === "PARTIAL" ? "warn" : "good"}>
                      {o.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-2xs text-slate-500">
                    {new Date(o.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      onClick={() => cancel.mutate(o.id)}
                      disabled={cancel.isPending}
                    >
                      Cancel
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function PlaceOrderModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { active } = useActiveAccount();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<OrderRequest>({
    symbol: "AAPL",
    asset_class: "EQUITY",
    side: "BUY",
    order_type: "MARKET",
    quantity: 1,
    time_in_force: "GTC",
    strategy_id: "manual",
  });

  const submit = useMutation({
    mutationFn: (body: OrderRequest) =>
      orders.create({ ...body, trading_account_id: active?.id }),
    onSuccess: (res) => {
      toast.push(`${res.message} (via ${res.routed_via})`, "success");
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
      qc.invalidateQueries({ queryKey: ["limit-orders"] });
      qc.invalidateQueries({ queryKey: ["margin"] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  function update<K extends keyof OrderRequest>(k: K, v: OrderRequest[K]) {
    setForm((cur) => ({ ...cur, [k]: v }));
  }

  return (
    <Modal open={open} onClose={onClose} title="Place order">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          submit.mutate(form);
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1 text-2xs text-term-dim">
          Trading account:{" "}
          {active ? (
            <span className="font-mono text-term-fg">
              {active.login_code}{active.label ? ` · ${active.label}` : ""}
            </span>
          ) : (
            <span className="warn">none — order will go through legacy broker path</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Symbol</Label>
            <Input value={form.symbol} onChange={(e) => update("symbol", e.target.value.toUpperCase())} />
          </div>
          <div>
            <Label>Asset class</Label>
            <Select value={form.asset_class} onChange={(e) => update("asset_class", e.target.value)}>
              <option>EQUITY</option>
              <option>FX</option>
              <option>CRYPTO</option>
            </Select>
          </div>
          <div>
            <Label>Side</Label>
            <Select value={form.side} onChange={(e) => update("side", e.target.value as "BUY" | "SELL")}>
              <option>BUY</option>
              <option>SELL</option>
            </Select>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.order_type} onChange={(e) => update("order_type", e.target.value as OrderRequest["order_type"])}>
              <option>MARKET</option>
              <option>LIMIT</option>
              <option>STOP</option>
              <option>STOP_LIMIT</option>
            </Select>
          </div>
          <div>
            <Label>Quantity</Label>
            <Input type="number" step="any" value={form.quantity} onChange={(e) => update("quantity", Number(e.target.value))} />
          </div>
          {(form.order_type === "LIMIT" || form.order_type === "STOP_LIMIT") && (
            <div>
              <Label>Limit price</Label>
              <Input type="number" step="any" value={form.limit_price ?? ""} onChange={(e) => update("limit_price", Number(e.target.value))} />
            </div>
          )}
          {(form.order_type === "STOP" || form.order_type === "STOP_LIMIT") && (
            <div>
              <Label>Stop price</Label>
              <Input type="number" step="any" value={form.stop_price ?? ""} onChange={(e) => update("stop_price", Number(e.target.value))} />
            </div>
          )}
          <div>
            <Label>Time in force</Label>
            <Select
              value={form.time_in_force ?? "GTC"}
              onChange={(e) =>
                update("time_in_force", e.target.value as OrderRequest["time_in_force"])
              }
            >
              <option value="GTC">GTC — good till cancelled</option>
              <option value="DAY">DAY — expires at session close</option>
              <option value="IOC">IOC — immediate-or-cancel</option>
              <option value="FOK">FOK — fill-or-kill</option>
            </Select>
          </div>
          <div>
            <Label>Stop loss (optional)</Label>
            <Input
              type="number"
              step="any"
              value={form.stop_loss ?? ""}
              onChange={(e) =>
                update(
                  "stop_loss",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              placeholder="attached after fill"
            />
          </div>
          <div>
            <Label>Take profit (optional)</Label>
            <Input
              type="number"
              step="any"
              value={form.take_profit ?? ""}
              onChange={(e) =>
                update(
                  "take_profit",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              placeholder="attached after fill"
            />
          </div>
        </div>
        <FormError>{error}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submit.isPending}>
            Submit
          </Button>
        </div>
      </form>
    </Modal>
  );
}
